import {
  GeneratedForm,
  GeneratedWorksheet,
} from './documents/worksheet-form-doc';
import { PolicyContext } from './scp-policy';
import { UIFieldData } from './policy-results/field-data';
import {
  AlertResult,
  DocumentStatusResult,
  FaultResult,
  FillerFormFieldResult,
  FootnoteResult,
  FormListResult,
  InternalErrorResult,
  MessageResult,
  NewTransactionErrorResult,
  OpenResult,
  ProgressStartedResult,
  RatingCompletedResult,
  RatingMessageResult,
  RatingStartedResult,
  UINavigation,
  UIRecordStatus,
  UIScreenDefinition,
  WorksheetListResult,
} from './policy-results/policy-result';
import { PolicyRatingResults } from './policy-results/rating-results';
import {
  UIFormSelectInfo,
  UIPolicySummary,
} from './policy-results/ui/ui-result-models';

export type PolicyServiceStatus = 'running' | 'stopping' | 'stopped';

export enum FormApplicability {
  All = 0,
  OnlyStateOfDomicile = 1,
  OnlyStatesOnPolicy = 2,
  AllStates = 3,
  OnPolicy = 4,
}

export interface PolicyBusy {
  // Has the app UI indicated that the app is busy
  busyCount: number;
  // Is a command on the policy request channel in progess (waiting for ActionCompleteResult)
  isCommandInProgress: boolean;
}

/**
 * A session with with the policy service identified by client-generated PolicyContext.PolicyId
 */
export interface ServerPolicy {
  context: PolicyContext;
  busy: PolicyBusy;
  isGeneratingWorksheetDoc?: boolean;
  isGeneratingFormDoc?: boolean;
  isGeneratingRating?: boolean;
  serviceStatus: PolicyServiceStatus;
  openResult?: OpenResult;
  loadStatus: {
    status: 'unopened' | 'opened' | 'open-temp-save-required' | 'closed';
    // Filled in when status is open-temp-save-required
    recoverableId?: string;
  };
  // Stack of screens to display. There is typically only one screen to show,
  // but when dialogs are opened and closed, elements will be added/removed
  // to the stack. The topmost dialog is the last element in the array.
  screenStack: Array<ScreenDefinitionModel>;
  // Current policy navigation. This supercedes any navigation that might be found
  // within a particular screen in the screen stack
  navigation: UINavigation;
  // Map of all fields for all screens, keyed by UIFieldData.Name
  fieldMap: Record<string, UIFieldData>;
  /**
   * record statuses across all screens by ID (qQSDocument.uiRecordStatuses)
   *
   * cumulative across screen updates (see qQSDocument.processScreen)
   */
  recordStatusMap: Record<string, UIRecordStatus>;
  internalErrors: Array<InternalErrorResult>;
  ratingResultMessages: Array<RatingMessageResult>;
  faults: Array<FaultResult>;
  newTransactionMessages: Array<NewTransactionErrorResult>;
  policyRatings?: PolicyRatingResults;
  policyRatingStarted?: RatingStartedResult;
  policyRatingCompleted?: RatingCompletedResult;
  docStatus?: DocumentStatusResult;
  summary?: UIPolicySummary;
  currentWorksheetList?: WorksheetListResult;
  currentFormList?: FormListResult;
  currentDocAlertResult?: AlertResult;
  generatedDocuments?: Array<GeneratedForm | GeneratedWorksheet>;
  footnotes?: Record<number, FootnoteResult>;
  policyFormInfo?: {
    formsOnPolicy: Array<UIFormSelectInfo>;
    forms: Array<UIFormSelectInfo>;
    applicabilityFilter: Array<FormApplicability>;
  };
  // Use getFormKey to get the lookup key
  fillerForms: Record<string, FillerFormFieldResult>;
  // An ordered list of messages associated w/ policy
  messageQueue: MessageResult[];
  currentPolicyProgress?: ProgressStartedResult;
  // When focusFieldId has a value, focus should be set to that field (most likely
  // during error navigation). Once focus has been set, this field should be cleared.
  focusFieldId?: string;
}

export type ScreenDefinitionModel = Omit<UIScreenDefinition, 'RecordStatuses'>;

export function createInitialServerPolicy(
  context: PolicyContext,
): ServerPolicy {
  return {
    context,
    serviceStatus: 'running',
    screenStack: [],
    navigation: { LOBs: [] },
    fieldMap: {},
    recordStatusMap: {},
    loadStatus: { status: 'unopened' },
    internalErrors: [],
    ratingResultMessages: [],
    faults: [],
    newTransactionMessages: [],
    policyFormInfo: {
      formsOnPolicy: [],
      forms: [],
      applicabilityFilter: [],
    },
    fillerForms: {},
    // list of messages, w/ messageQueue[0] being the oldest (and first to show)
    messageQueue: [],
    busy: {
      busyCount: 0,
      isCommandInProgress: false,
    },
  };
}

export function isServerPolicyDirty(sp: ServerPolicy | undefined): boolean {
  return true === sp?.docStatus?.DocumentModified;
}

/** currently executing a command or an app-defined blocking/busy operation */
export function isServerPolicyBusy(sp: ServerPolicy | undefined): boolean {
  return Boolean(sp && (sp.busy.isCommandInProgress || sp.busy.busyCount > 0));
}

/** app-loading-indicator in a few sections */
export function showServerPolicyLoadingIndicator(
  sp: ServerPolicy | undefined,
): boolean {
  return sp?.isGeneratingFormDoc || sp?.isGeneratingWorksheetDoc
    ? false
    : isServerPolicyBusy(sp);
}

// Returns the base active screen (that is, the screen at the bottom of the screen stack)
// for the server policy.
export function getBaseActiveScreen(
  sp?: ServerPolicy | undefined,
): ScreenDefinitionModel | undefined {
  return sp?.screenStack[0];
}

// Returns an active screen in the screen stack. If offset is 0, then the screen
// at the top of the stack is returned. Otherwise the screen at the specified offset
// from the top of the stack is returned. For example, an offset of -1 means the screen
// immediately below the screen at the top the stack.
export function getActiveScreen(
  serverPolicy?: ServerPolicy | null,
  offset: number = 0,
): ScreenDefinitionModel | undefined {
  if (!serverPolicy?.screenStack) return undefined;
  if (offset > 0) throw new Error('Offset must be <= 0');

  if (serverPolicy?.screenStack.length == 0) {
    return undefined;
  }
  return serverPolicy.screenStack[serverPolicy.screenStack.length - 1 + offset];
}
